import type { EChartsOption } from "echarts";
import mustache from "mustache";
import { EChartRender } from "../../../../implementations/chart/EChartRender";
import { CompraAnimalesResponse } from "../../../../../modulos/pesaje/ganaderia/response/CompraAnimalesResponse";
import { VentaAnimalesResponse } from "../../../../../modulos/pesaje/ganaderia/response/VentaAnimalesResponse";
import { formatMesAnioCorto, mesesAtras } from "../../../../../utils/fechaUtils";
import { _miles } from "../../../../../utils/numberUtils";
import template from "./resultadoHistoricoCardTemplate.html";

mustache.parse(template);

/** Barra del chart: una venta, con su etiqueta y los dos montos a comparar. */
type BarraHistorico = {
  fecha: string;
  label: string;
  costoCompra: number;
  ingresoVenta: number;
};

const MESES_A_MOSTRAR = 6;

/** Card "Resultado histórico por lote": costo de compra vs ingreso de venta
 *  de los últimos 6 meses. No existe en el modelo un vínculo compra↔venta, así
 *  que el costo de compra es una heurística: la compra más reciente, del
 *  mismo potrero (`loteActual`/`loteSalida`), anterior o igual a la fecha de
 *  venta ("la compra más cercana"). */
export class ResultadoHistoricoCardRender extends EChartRender {
  private readonly _barras: BarraHistorico[];

  constructor(
    private readonly _compras: CompraAnimalesResponse[],
    ventas: VentaAnimalesResponse[],
  ) {
    super();
    this._barras = this._construirBarras(ventas);
  }

  override render(): string {
    return mustache.render(template, {
      hasDatos: this._barras.length > 0,
      chart: super.render(),
    });
  }

  private _construirBarras(ventas: VentaAnimalesResponse[]): BarraHistorico[] {
    const desde = mesesAtras(MESES_A_MOSTRAR);
    const barras: BarraHistorico[] = [];
    for (const venta of ventas) {
      if (venta.fechaVenta < desde) continue;
      const compra = this._compraMasCercana(venta);
      barras.push({
        fecha: venta.fechaVenta,
        label: `${venta.loteSalida.nombreLote} ${formatMesAnioCorto(venta.fechaVenta)}`,
        costoCompra: compra?.precioTotal ?? 0,
        ingresoVenta: venta.precioTotal,
      });
    }
    return barras.sort((a, b) => a.fecha.localeCompare(b.fecha));
  }

  /** Compra del mismo potrero, con `fechaCompra` más reciente sin pasarse de
   *  la fecha de venta. `null` si no hay ninguna (dato inconsistente). */
  private _compraMasCercana(
    venta: VentaAnimalesResponse,
  ): CompraAnimalesResponse | null {
    const candidatas = this._compras.filter(
      (c) =>
        c.loteActual.id === venta.loteSalida.id &&
        c.fechaCompra <= venta.fechaVenta,
    );
    if (candidatas.length === 0) return null;
    return candidatas.reduce((masReciente, c) =>
      c.fechaCompra > masReciente.fechaCompra ? c : masReciente,
    );
  }

  protected getOption(): EChartsOption {
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        valueFormatter: (v) => `$${_miles(Math.round(Number(v)))}`,
      },
      legend: { bottom: 0 },
      grid: { left: 64, right: 16, top: 24, bottom: 40 },
      xAxis: { type: "category", data: this._barras.map((b) => b.label) },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v: number) => `$${_miles(v)}` },
      },
      series: [
        {
          name: "Costo compra",
          type: "bar",
          data: this._barras.map((b) => b.costoCompra),
        },
        {
          name: "Ingreso venta",
          type: "bar",
          data: this._barras.map((b) => b.ingresoVenta),
        },
      ],
    };
  }
}
